import { Box, Button, Card, CardContent, FormControlLabel, FormGroup, FormHelperText, Grid, Link, MenuItem, Switch, Tooltip, Typography } from "@mui/material";
import { useFormik } from "formik";
import { RecordMap, ReduxState, useDelete, useRecordContext, crudGetList, crudGetOne } from "ra-core";
import { FC, MutableRefObject, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import SelectField from "@/components/formik/SelectField";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import Edit from "@/controller/Edit";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { CloudProvider, CloudSubscriptionResponseDto, TestResultResponseDto } from "@/gen/api/user";
import { OdResourcePageProps } from "@/types/resource";
import { CloudRegionsMap } from "./cloudRegions";
import useIdTokenUser from "@/authProvider/useIdTokenUser";
import ReadonlyField from "@/components/input/ReadonlyField";
import { useDispatch, useSelector } from "react-redux";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { RecPatch } from "@/dataProvider/tools";
import { useResourceRegister } from "@/utils/resourceRegister";
import { useSnackbar } from "notistack";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save"
import { autoCopyTextFieldProps } from "@/utils/clipboard";
import { pick } from "lodash";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { CLOUD_MANAGEMENT } from "@/constants";
import { crudGetOneEx } from "@/actions/crudFetchEx";


const compile_target = import.meta.env.VITE_DEPLOY_TARGET


const validationSchema =
  Yup.object().shape({
    cloudProvider: Yup.number().required('Cloud Provider is required'),
    resourceGroupName: Yup.string().max(100).required('Resource Group Name is required'),
    region: Yup.string().max(100).required('Region is required'),
    tenantId: Yup.string().max(100).required('Tenant Id is required'),
    subscriptionId: Yup.string().max(100).required('Subscription Id is required'),
  })

const getInitialValues = (preData?: CloudSubscriptionResponseDto): Values => ({
  cloudProvider: preData?.cloudProvider || CloudProvider.AzureUSGovernment,
  resourceGroupName: preData?.resourceGroupName || '',
  region: preData?.region || 'usgovtexas',
  tenantId: preData?.tenantId || '',
  subscriptionId: preData?.subscriptionId || '',
})

interface Values {
  cloudProvider: CloudProvider,
  resourceGroupName: string,
  region: string,
  tenantId: string,
  subscriptionId: string,
}

const CloudSubscriptionUpdate: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: 'settings-cloud-subscription-consent-url' })
  useResourceRegister({ name: 'settings-cloud-subscription-test' })

  const { resource } = props
  const handlers = useDefaultSavedHandlers("Update Cloud Subscription")
  return (
    <Edit resource={resource} basePath="" mutationMode="pessimistic" id="1" {...handlers}>
      <CloudSubscriptionUpdateBody {...props} />
    </Edit>
  )
}


interface PageState {
  isEnabled: boolean,
  consentUrl: string,
  testConfig: {
    result: TestResultResponseDto|undefined|null,
    loading: boolean,
    reqTime: Date,
    hash: string,
  },
  applyingToInfras: boolean,
}
type Action =
| {type: 'performTest', payload: any }
| {type: 'testResultFetched', payload: TestResultResponseDto|undefined }
| {type: 'testResultStaled' }
| {type: 'updateConsentUrl', payload: string }
| {type: 'setIsEnabled', payload: boolean }
| {type: 'toggleEnabled' }
| {type: 'applyConfigChange' }
| {type: 'configChangeApplied' }

const buildReducer = ({ dispatchEffect, preDataRef: { current: preData }, enqueueSnackbar }: {
  dispatchEffect: (x:any)=>void,
  enqueueSnackbar: (x:any, y:any)=>void,
  preDataRef: MutableRefObject<CloudSubscriptionResponseDto|null>,
}) => (prevState: PageState, action: Action): PageState => {
  switch(action.type) {
    case 'performTest': {
      dispatchEffect(crudGetList(
        'settings-cloud-subscription-test',
        pages(), sorts(),
        { ...action.payload }
      ))
      const payloadAbstract = pick(action.payload, [
        'cloudProvider', 'resourceGroupName', 'region', 'tenantId', 'subscriptionId'
      ])
      return {
        ...prevState,
        testConfig: {
          ...prevState.testConfig,
          loading: true,
          reqTime: new Date(),
          hash: JSON.stringify(payloadAbstract)
        }
      }
    }
    case 'testResultFetched': {
      if (action.payload?.isSuccess) {
        setTimeout(() => 
          enqueueSnackbar('Test Configuration succeed.', { variant: 'success' }))
      }
      return {
        ...prevState,
        testConfig: {
          ...prevState.testConfig,
          result: action.payload,
          loading: false,
        }
      }
    }
    case 'testResultStaled': {
      return {
        ...prevState,
        testConfig: { result: undefined, reqTime: new Date(), loading: false, hash: '{}', }
      }
    }
    case 'updateConsentUrl': {
      return {
        ...prevState,
        consentUrl: action.payload,
      }
    }
    case 'setIsEnabled': {
      return {
        ...prevState,
        isEnabled: action.payload,
        testConfig: { result: undefined, loading: false, reqTime: new Date(), hash: '{}' },
      }
    }
    case 'toggleEnabled': {
      return {
        ...prevState,
        isEnabled: !prevState.isEnabled,
        testConfig: { result: undefined, loading: false, reqTime: new Date(), hash: '{}' },
      }
    }
    case 'applyConfigChange': {
      if (prevState.isEnabled && !preData?.storageAccountName) {
        return {
          ...prevState,
          applyingToInfras: true,
          testConfig: { result: undefined, loading: false, reqTime: new Date(), hash: '{}' },
        }
      } else {
        return prevState
      }
    }
    case 'configChangeApplied': {
      setTimeout(() =>
        enqueueSnackbar('Infrastructure ready for new Cloud Subscription', {variant: 'success'}))
      return {
        ...prevState,
        applyingToInfras: false,
      }
    }
  }
  return prevState
}


const CloudSubscriptionUpdateBody: FC<OdResourcePageProps> = ({ resource }) => {
  const idTokenUser = useIdTokenUser()
  const hasWritePermit = useHasPermit([CLOUD_MANAGEMENT])

  const save = useSimpleSaveAction()
  const preData = useRecordContext<CloudSubscriptionResponseDto>()
  const initialValues = {
    submit: '',
    ...getInitialValues(preData),
  }

  const preDataRef = useRef<CloudSubscriptionResponseDto | null>(preData)

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    if (!hasWritePermit) { return }
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
        dispatch({ type: 'applyConfigChange' })
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }
    const payload = {
      ...values, 
      organizationId: idTokenUser?.organization_id, 
      clientId: "",
    }
    save(payload, options)
  }, [save, idTokenUser, hasWritePermit])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { values, errors, isSubmitting, handleChange, setTouched, setValues, setFieldValue, setFormikState } = formikCtrl

  const formikCtrlRef = useRef(formikCtrl)
  formikCtrlRef.current = formikCtrl

  const pageLoaded = useRef(false)
  useEffect(() => {
    if (preDataRef.current !== preData) {
      preDataRef.current = preData
      
      if (!pageLoaded.current) {
        pageLoaded.current = true
        setValues({
          cloudProvider: preData.cloudProvider || CloudProvider.AzureUSGovernment,
          resourceGroupName: preData.resourceGroupName || '',
          region: preData.region || 'usgovtexas',
          tenantId: preData.tenantId || '',
          subscriptionId: preData.subscriptionId || '',
        } as any)
        setIsEnabled(!!preData.isEnabled)
      }
    }
  }, [preData, setValues])

  const disableHandlers = useDefaultSavedHandlers("Update Cloud Subscription")
  disableHandlers.onSuccess.merge(useCallback(() => {
    toggleEnabled()
  }, []))
  const [sendDisable, { loading: disableRequesting }] = useDelete(resource, 2, undefined, disableHandlers)

  const handleSwitchChange = () => {
    if (states.isEnabled) { 
      if (preData.createdTime) {
        sendDisable() 
      } else {
        setIsEnabled(false)
      }
      setFormikState(state => ({ 
        ...state, 
        errors: {}, 
        touched: {}, 
        values: { 
          ...state.values, 
          ...getInitialValues(preData),
        } 
      }))
    }
    else {
      setIsEnabled(true)
    }
  }

  const dispatchEffect = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    if (values.cloudProvider && values.tenantId) {
      dispatchEffect(crudGetList(
        'settings-cloud-subscription-consent-url',
        pages(), sorts(),
        { cloudProvider: values.cloudProvider, tenantId: values.tenantId }
      ))
    }
  }, [values.cloudProvider, values.tenantId, dispatchEffect])  

  const reducer = useMemo(() => buildReducer({dispatchEffect, preDataRef, enqueueSnackbar}), [dispatchEffect])

  const [states, dispatch] = useReducer(reducer, {
    isEnabled: !!preData?.isEnabled,
    consentUrl: '',
    testConfig: { loading: false, result: undefined, reqTime: new Date(), hash: '{}' },
    applyingToInfras: false,
  })

  const { setIsEnabled, toggleEnabled } = useMemo(() => ({
    setIsEnabled: (x:boolean) => dispatch({ type: 'setIsEnabled', payload: x }),
    toggleEnabled: () => dispatch({ type: 'toggleEnabled' }),
  }), [dispatch])

  useSelector<ReduxState, any>(store => {
    const testConfigResultData : RecordMap<TestResultResponseDto&RecPatch> = 
      store.admin.resources['settings-cloud-subscription-test']?.data
    const result : TestResultResponseDto&RecPatch | undefined = testConfigResultData && testConfigResultData[1]
    const fetchedAt : Date|undefined = testConfigResultData ? (testConfigResultData.fetchedAt as any)[1] : undefined
    if (result && states.testConfig.loading && fetchedAt && states.testConfig.reqTime < fetchedAt) {
      dispatch({ type: 'testResultFetched', payload: result })
    }
    const consentUrl = store.admin.resources['settings-cloud-subscription-consent-url']?.data[1] as any
    if (consentUrl?.url !== states.consentUrl) {
      dispatch({ type: 'updateConsentUrl', payload: consentUrl?.url })
    }
  })

  useEffect(() => {
    let timer: number|null = null
    if (states.applyingToInfras) {
      timer = window.setInterval(() => {
        dispatchEffect(crudGetOneEx(resource || 'noop', 1, ''))
      }, 10000)
    } else {
      if (timer) {
        clearInterval(timer)
        timer = null
      }
    }
    return () => {
      timer && clearInterval(timer)
    }
  }, [states.applyingToInfras, dispatchEffect, resource])

  useEffect(() => {
    if (states.applyingToInfras) {
      if (!states.isEnabled || preData?.storageAccountName) {
        dispatch({ type: 'configChangeApplied' })
      }
    }
  }, [states.isEnabled, states.applyingToInfras, preData])

  useEffect(() => {
    if (states.testConfig.hash !== '{}') {
      const valuesAbstract = pick(values, [
        'cloudProvider', 'resourceGroupName', 'region', 'tenantId', 'subscriptionId'
      ])
      const hash = JSON.stringify(valuesAbstract)
      if (hash !== states.testConfig.hash) {
        dispatch({ type: 'testResultStaled' })
      }
    }
  }, [values, states.testConfig.hash])

  let clickedOnce = false
  const [copied, setCopied] = useState(false)

  return (
    <OdForm formikCtrl={formikCtrl}>

      <Card>
        <CardContent>
          <Grid
            container
            spacing={4}
          >
            <Grid item xs={12} sx={{ position: 'relative' }} >
              <FormGroup sx={{ display: 'block', '& span.MuiTouchRipple-root': { color: 'primary.main' } }}>
                <FormControlLabel 
                  control={
                    <Switch name="isEnabled" checked={states.isEnabled} onChange={handleSwitchChange} disabled={!hasWritePermit || disableRequesting} />
                  } 
                  label="Use My Subscription" 
                />
              </FormGroup>
              {!(preData?.createdTime && preData.isEnabled) && states.isEnabled && <Typography variant="caption" sx={{ top: 40, left: 225, position: 'absolute' }} color="error">*</Typography>}
            </Grid>
            {(preData?.createdTime || states.isEnabled) &&
              <>
                <Grid item xs={12} md={4} >
                  <SelectField 
                    label="Cloud Provider*" 
                    name="cloudProvider" 
                    disabled={!hasWritePermit || !states.isEnabled} 
                    onChange={(e:any) => { setFieldValue('region', ''); handleChange(e) }}
                  >
                    <MenuItem value={CloudProvider.AzureUSGovernment}>Azure US Government</MenuItem>
                    {compile_target === 'test' && <MenuItem value={CloudProvider.Azure}>Azure</MenuItem>}
                  </SelectField>
                </Grid>
                <Grid item xs={12} md={4} >
                  <SelectField label="Region*" name="region" disabled={!hasWritePermit || !states.isEnabled} >
                    {(CloudRegionsMap[values.cloudProvider] || []).map(r => (
                      <MenuItem key={r.name} value={r.name} disabled={values.cloudProvider === CloudProvider.AzureUSGovernment && r.name !== 'usgovtexas'}>
                        {r.displayName} 
                      </MenuItem>
                    ))}
                  </SelectField>
                </Grid>
                <Grid item xs={12} >
                  <TextField label="Tenant Id*" name="tenantId" disabled={!hasWritePermit || !states.isEnabled} />
                </Grid>
                <Grid item xs={12} >
                  <TextField label="Subscription Id*" name="subscriptionId" disabled={!hasWritePermit || !states.isEnabled} />
                </Grid>
                <Grid item xs={12} >
                  <TextField label="Resource Group Name*" name="resourceGroupName" disabled={!hasWritePermit || !states.isEnabled} />
                </Grid>
                <Grid item xs={12} className={values.cloudProvider && values.tenantId ? 'showConsentUrl' : undefined} >
                  <Tooltip title={states.consentUrl ? copied ? 'Copied' : "Click to Copy" : ''} placement="top">
                    <Box>
                      <ReadonlyField 
                        label="Cloud Platform Consent Url" 
                        name="consentUrl" 
                        value={states.consentUrl || ''}
                        {...autoCopyTextFieldProps(() => setCopied(true), () => setTimeout(() => setCopied(false), 500))}
                        sx={{
                          '& input': { cursor: states.consentUrl ? 'pointer' : 'default'},
                        }}                        
                      />
                    </Box>
                  </Tooltip>
                  <FormHelperText sx={{ ml: 2 }}>
                    Please open up this generated url in browser, and grant OnData access to your cloud subscription.
                  </FormHelperText>
                </Grid>
              </>
            }
          </Grid>
          {states.isEnabled && (errors.submit 
          || (!states.testConfig.loading && states.testConfig.result?.isSuccess === false)) && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>
                {errors.submit || states.testConfig.result?.message}
              </FormHelperText>
            </Box>
          )}
        </CardContent>
        {(preData?.createdTime || states.isEnabled) && hasWritePermit &&
          <Box sx={{ p: 2 }} >
            <Button
              color="primary"
              disabled={isSubmitting || !states.isEnabled || states.testConfig.loading}
              variant="outlined"
              sx={{ mr: 2 }}
              onClick={() => setTouched({
                resourceGroupName: true,
                region: true,
                tenantId: true,
                subscriptionId: true,
              }, true).then((errors:any) => {
                if ((errors && Object.keys(errors).length > 0) || clickedOnce) { return }
                clickedOnce = true
                dispatch({ type: 'performTest', payload: {...values, organizationId: idTokenUser?.organization_id } })
              })}
            >
              Test Configuration
            </Button>
            <LoadingButton
              color="primary"
              disabled={isSubmitting || !states.isEnabled || !states.testConfig.result?.isSuccess}
              type="submit"
              variant="contained"
              loading={states.applyingToInfras}
              loadingPosition="start"
              startIcon={<SaveIcon />}
            >
              {states.applyingToInfras 
              ? 'Applying Changes to Infrastructure...'
              : 'Update Cloud Subscription'}
            </LoadingButton>
          </Box>
        }
      </Card>

    </OdForm>
  )
}

export default CloudSubscriptionUpdate;
